import { Table, TableBody, TableCell, TableRow } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { DynamoDbBillingModes } from '@/data/inventory/data';

interface TableProperties {
  billingMode?: string;
  readCapacityUnits?: number;
  writeCapacityUnits?: number;
}

const getNoSqlTableProperties = (
  resource: InventoryResource
): TableProperties | undefined =>
  resource.resourceProperties?.awsDynamoDb ??
  resource.resourceProperties?.awsKeyspacesTable ??
  undefined;

export const NoSqlTablePropertiesSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const properties = getNoSqlTableProperties(resource);

  return (
    <Table
      className='w-full'
      sx={{
        '& .props-col': {
          verticalAlign: 'top',
          width: '225px',
          paddingLeft: '40px',
        },
      }}
    >
      <TableBody>
        {properties?.billingMode &&
          DynamoDbBillingModes[properties.billingMode]?.name && (
            <TableRow>
              <TableCell
                className='props-col'
                sx={{
                  borderTop: '1px solid var(--mui-palette-divider)',
                }}
              >
                Capacity mode
              </TableCell>
              <TableCell
                sx={{
                  borderTop: '1px solid var(--mui-palette-divider)',
                }}
              >
                {DynamoDbBillingModes[properties.billingMode].name}
              </TableCell>
            </TableRow>
          )}
        {typeof properties?.readCapacityUnits === 'number' && (
          <TableRow>
            <TableCell className='props-col'>Read units</TableCell>
            <TableCell>{properties.readCapacityUnits}</TableCell>
          </TableRow>
        )}
        {typeof properties?.writeCapacityUnits === 'number' && (
          <TableRow>
            <TableCell className='props-col'>Write units</TableCell>
            <TableCell>{properties.writeCapacityUnits}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export const NoSqlTableBackupConfigurationSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const isKeyspaces = !!resource.resourceProperties?.awsKeyspacesTable;
  return isKeyspaces ? (
    <KeyspacesBackupConfigSection resourceId={resource.id} />
  ) : (
    <DynamoDbBackupConfigSection resourceId={resource.id} />
  );
};

const DynamoDbBackupConfigSection = ({
  resourceId,
}: {
  resourceId: string;
}) => {
  const dal = useDAL();
  const { data: configResponse } =
    dal.inventory.backupConfiguration.dynamodb.useConfig(resourceId);

  if (!configResponse) {
    return null;
  }

  return <BackupConfigTable configResponse={configResponse} />;
};

const KeyspacesBackupConfigSection = ({
  resourceId,
}: {
  resourceId: string;
}) => {
  const dal = useDAL();
  const { data: configResponse } =
    dal.inventory.backupConfiguration.keyspaces.useConfig(resourceId);

  if (!configResponse) {
    return null;
  }

  return <BackupConfigTable configResponse={configResponse} />;
};

const BackupConfigTable = ({
  configResponse,
}: {
  configResponse: {
    isAutoscaleEnabled: boolean;
    maxReadCapacityUnits?: number;
    readCapacityUnitsRequired: number;
  };
}) => {
  return (
    <Table
      className='w-full'
      sx={{
        '& .props-col': {
          verticalAlign: 'top',
          width: '225px',
          paddingLeft: '40px',
        },
      }}
    >
      <TableBody>
        <TableRow>
          <TableCell className='props-col'>Auto-adjust</TableCell>
          <TableCell>
            {configResponse.isAutoscaleEnabled ? 'On' : 'Off'}
          </TableCell>
        </TableRow>
        {typeof configResponse.maxReadCapacityUnits === 'number' && (
          <TableRow>
            <TableCell className='props-col'>Maximum read units</TableCell>
            <TableCell>{configResponse.maxReadCapacityUnits}</TableCell>
          </TableRow>
        )}
        <TableRow>
          <TableCell className='props-col'>
            Read units required for backup
          </TableCell>
          <TableCell>{configResponse.readCapacityUnitsRequired}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
